import { Alert, Text } from '@ksyos/design-system';
import { useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IServiceStartingPoint } from '@common/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import { CustomControlCode, EpisodeCategory } from '@common/enums';
import { getServiceStartingPoints } from '@frontend/services/api/episodeCategoriesApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';

export function OsasRenewedAlert() {
    const abortSignal = useAbortSignal();
    const { servicePermissions } = useSelector((state: IReduxState) => state.authentication);
    const [showOsasBanner, setShowOsasBanner] = useState<boolean>(false);
    const [hideOsasRenewedAlert, setHideOsasRenewedAlert] = useState<boolean>(false);
    const { episodeCategory, patient } = usePatientEpisodeCategoryContext();
    const [searchParams] = useSearchParams();
    const startingPointId = searchParams.get('startingPointId');

    useEffect(() => {
        loadStartingPointAndShowBanner();
    }, []);

    useEffect(() => {
        if (hideOsasRenewedAlert) {
            loadStartingPointAndShowBanner();
        }
    }, [hideOsasRenewedAlert]);

    if (!showOsasBanner) {
        return null;
    }

    return (
        <Alert
            appearance="info"
            hasIcon={true}
            isClosable={true}
            onClose={() => {
                localStorage.setItem('hideOsasRenewedAlert', 'true');
                setHideOsasRenewedAlert(true);
            }}
            style={{ marginBottom: 'var(--kds-spacing-8)' }}
        >
            <Text
                as="p"
                color="inherit"
                fontWeight="bold"
                mb={2}
                size="md"
            >
                Het verwijsformulier is vernieuwd!
            </Text>
            <Text
                as="p"
                color="inherit"
                size="md"
            >
                Het formulier is korter geworden. De anamnese wordt behandeld in het medisch consult met de patiënt.
            </Text>
        </Alert>
    );

    async function loadStartingPointAndShowBanner(): Promise<void> {
        if (
            !startingPointId ||
            episodeCategory !== EpisodeCategory.somnology ||
            !servicePermissions?.OSAS?.canStart ||
            Boolean(localStorage.getItem('hideOsasRenewedAlert')) ||
            hideOsasRenewedAlert
        ) {
            setShowOsasBanner(false);

            return;
        }

        const resultStartingPoints = await getServiceStartingPoints(episodeCategory, patient.id, abortSignal);

        const nextStartingPoint = resultStartingPoints.find(
            (startingPoint: IServiceStartingPoint) =>
                startingPoint.id === startingPointId &&
                startingPoint.activities[0].customControlCode === CustomControlCode.OSASReferral
        );

        if (nextStartingPoint) {
            setShowOsasBanner(true);
        }
    }
}
